import { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/layout/PageHero";
import SEO from "@/components/layout/SEO";
import FeatureList from "@/components/layout/FeatureList";
import FinalCTA from "@/components/home/FinalCTA";
import { motion } from "framer-motion";
import {
  Download,
  ExternalLink,
  Copy,
  Check,
  BadgeCheck,
  Globe2,
  Award,
  Languages,
  ScrollText,
} from "lucide-react";
import { LANGUAGECERT, IMG } from "@/lib/content";

const REQUIREMENTS = [
  {
    title: "Most programs require B2",
    body: "Bachelor and Master programs taught in English typically require CEFR B2. Some Medicine, Dentistry and selective programs require C1.",
  },
  {
    title: "Official accredited exams",
    body: "Polish universities accept IELTS, TOEFL, Cambridge, Pearson PTE — and the increasingly popular LanguageCert International ESOL.",
  },
  {
    title: "When to take it",
    body: "Book your test 4–6 months before your application deadline. Results take 7–14 days; reschedules are usually allowed.",
  },
  {
    title: "Score validity",
    body: "Most certificates are valid for 2 years from issue. Some universities accept slightly older results — always check the program page.",
  },
];

function CopyableCode({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Discount code copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid="languagecert-discount-code">
      <code className="font-mono font-bold text-2xl md:text-3xl tracking-[0.2em] text-white bg-black/30 px-5 py-3 rounded-xl border border-white/10">
        {code}
      </code>
      <button
        onClick={copy}
        className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        aria-label="Copy code"
        data-testid="copy-discount-code"
      >
        {copied ? <Check className="w-4 h-4 text-white" /> : <Copy className="w-4 h-4 text-white" />}
      </button>
    </div>
  );
}

export default function EnglishExam() {
  return (
    <div data-testid="page-english-exam">
      <SEO
        title="English language exams for studying in Poland"
        description="English requirements for Polish university programs in English: accredited exams, LanguageCert ESOL guidance, and an official discount code for international applicants."
        path="/english-exam"
      />
      <PageHero
        overline="English Language Requirements"
        title={
          <>
            English requirements to study in Poland — <span className="italic text-[#D7141A]">with the right exam</span>.
          </>
        }
        subtitle="Most English-taught programs in Poland require CEFR B2 or C1. The good news: there are several officially accredited routes, and a couple are faster and friendlier than the names you usually hear."
        image={IMG.englishExam}
      />

      {/* Quick requirements */}
      <FeatureList
        overline="The essentials"
        title="What Polish universities actually ask for."
        intro="Across the 100+ universities I work with in Poland, the same patterns repeat. Here's the short version."
        items={REQUIREMENTS}
      />

      {/* LanguageCert hero card */}
      <section className="section-pad pt-0" data-testid="english-languagecert">
        <div className="container-sp">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2rem] border border-[#1B2A3D] bg-[#0D1B2A] text-white p-8 md:p-14 grid md:grid-cols-12 gap-10 items-center"
          >
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-30"
              style={{ background: "radial-gradient(circle, #D7141A 0%, transparent 70%)" }}
            />

            <div className="md:col-span-7 relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/80 mb-6">
                <BadgeCheck className="w-3.5 h-3.5 text-[#E5444A]" />
                Officially accredited for Polish universities
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                LanguageCert International ESOL
              </h2>
              <p className="text-white/70 mt-5 leading-relaxed max-w-xl">
                One of the officially accredited English exams for international
                applicants to English-taught programs in Poland. Modular,
                widely-available, and recognised across Polish higher
                education — at Bachelor, Master and PhD level.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: Award, label: "Internationally recognised" },
                  { icon: Globe2, label: "Online & test-centre options" },
                  { icon: Languages, label: "B1 · B2 · C1 · C2 levels" },
                ].map((f) => (
                  <div key={f.label} className="flex items-start gap-2.5">
                    <f.icon className="w-4 h-4 text-[#E5444A] mt-0.5 shrink-0" />
                    <span className="text-sm text-white/85 leading-snug">{f.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={LANGUAGECERT.registerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold"
                  data-testid="languagecert-register-cta"
                >
                  Register on LanguageCert <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href={LANGUAGECERT.guidePdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3 text-sm font-medium hover:bg-white hover:text-[#0D1B2A] transition-all"
                  data-testid="languagecert-download-guide"
                >
                  <Download className="w-4 h-4" /> Download B2 Guide (PDF)
                </a>
              </div>
            </div>

            <div className="md:col-span-5 relative">
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-7">
                <div className="overline" style={{ color: "#E5444A" }}>Official discount code</div>
                <p className="text-sm text-white/70 mt-3 leading-relaxed">
                  Use our StudyPoland advisor code when you register, applicable
                  on eligible LanguageCert ESOL exam fees.
                </p>
                <div className="mt-6">
                  <CopyableCode code={LANGUAGECERT.discountCode} />
                </div>
                <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/60 leading-relaxed">
                  Apply the code at checkout on the LanguageCert portal.
                  If the field isn't visible, message us on WhatsApp and we'll
                  walk you through it.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other exams */}
      <section className="section-pad pt-0" data-testid="english-other-exams">
        <div className="container-sp grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline">Looking at other exams?</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D1B2A] mt-3 leading-tight">
              Compare accredited English exams.
            </h2>
            <p className="text-[#4A5568] mt-5 leading-relaxed">
              LanguageCert isn't the only route. Polish universities accept a
              range of accredited certificates — IELTS, TOEFL, Cambridge,
              Pearson PTE and more. Browse the full list of accepted exams and
              codes, and choose the one that fits your timeline and budget.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="card-sp">
              <ScrollText className="w-8 h-8 text-[#D7141A]" />
              <h3 className="font-serif text-2xl text-[#0D1B2A] mt-5">
                Browse other accredited exams
              </h3>
              <p className="text-sm text-[#4A5568] mt-3 leading-relaxed">
                See the full directory of LanguageCert-recognised exam codes and
                compare alternatives side by side.
              </p>
              <a
                href={LANGUAGECERT.otherExamsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-7"
                data-testid="languagecert-other-exams-cta"
              >
                Open exam directory <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
